import { useEffect } from "react";
import { useDispatch } from "react-redux";
import useMulticotizadorSelector from "./useMulticotizadorSelector";
import {
  clearEsPersonaValida,
  setEsPersonaValida,
  setPersona,
} from "../actions";
import { makeStyles } from "@mui/styles";
import checkIcon from "~assets/icons/ic-check.svg";
import closeIcon from "~assets/icons/ic-close.svg";
import { CheckboxButton } from "~components/styled/checkboxButton/CheckboxButton";
import Typography from "@mui/material/Typography";
import { useHistory } from "react-router-dom";
import ContentLoader from "react-content-loader";
import MulticotizadorContent from "./MulticotizadorContent";
import PrimaryTitle from "~styled/primaryTitle/PrimaryTitle";
import LoadingViewGears from "~styled/loadingView/LoadingViewGears";
import ListView from "~styled/listView/ListView";
import { PersonaIdentidad } from "../types";

const useStyles = makeStyles((theme) => ({
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-around",
    marginBottom: 20,
  },
  cotizador: {
    flexDirection: "row",
    margin: "auto",
    padding: 20,
    paddingBottom: 100,
    maxWidth: 500,
    width: "100%",
    minHeight: "100vh",
  },
  title: {
    paddingBottom: "16px",
  },
  container: {
    minHeight: 145,
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
  },
  btnLabel: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: "bold",
    lineHeight: 1.38,
    letterSpacing: 0.48,
    textAlign: "center",
    color: theme.palette.primary.main,
  },
  loading: { width: 100, height: 100 },
}));

const useReduxSelector = () =>
  useMulticotizadorSelector((state) => ({
    isLoading: state.isLoading,
    nombreSelected: state.nombreSelected,
    apellidoSelected: state.apellidoSelected,
    dniDuplicado: state.dniDuplicado,
  }));

const EsPersonaValidaStep = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { nombreSelected, apellidoSelected, dniDuplicado, isLoading } =
    useReduxSelector();

  useEffect(() => {
    dispatch(clearEsPersonaValida());
  }, [dispatch]);

  const failed = !isLoading && !nombreSelected;

  useEffect(() => {
    if (failed) {
      dispatch(setEsPersonaValida({ value: false, selected: true }));
    }
  }, [dispatch, failed]);

  const handleChange = (item: { value: unknown }) => {
    const persona = item.value as PersonaIdentidad;
    dispatch(
      setPersona({
        nombres: persona.nombres,
        apellido: persona.apellido,
        cuil: persona.cuil,
        fechaNacimiento: persona.fechaNacimiento,
        esPersonaValida: true,
      })
    );
  };

  if (isLoading || !nombreSelected) return <LoadingViewGears />;

  const content = dniDuplicado ? (
    <>
      <PrimaryTitle
        label={`Seleccioná el nombre del asegurado`}
        marginBottom={60}
      />
      <ListView
        onItemSelected={handleChange}
        list={labelizePersonas(dniDuplicado)}
      />
    </>
  ) : (
    <>
      <PrimaryTitle
        label={`¿El seguro es para ${nombreSelected} ${apellidoSelected}?`}
        marginBottom={60}
      />
      <div className={classes.checkboxContainer}>
        <div style={{ marginRight: 5, width: "100%" }}>
          <CheckboxButton
            onClick={() => {
              dispatch(setEsPersonaValida({ value: true, selected: true }));
            }}
          >
            <EsPersonaValidaButton label="SI" image={checkIcon} />
          </CheckboxButton>
        </div>

        <div style={{ marginLeft: 5, width: "100%" }}>
          <CheckboxButton
            onClick={() => {
              history.go(-2);
            }}
          >
            <EsPersonaValidaButton label="NO" image={closeIcon} />
          </CheckboxButton>
        </div>
      </div>
    </>
  );
  return <MulticotizadorContent>{content}</MulticotizadorContent>;
};

export default EsPersonaValidaStep;

interface EsPersonaValidaButtonProps {
  label: string;
  image: string;
  isLoading?: boolean;
}

const EsPersonaValidaButton = ({
  label,
  isLoading,
  image,
}: EsPersonaValidaButtonProps) => {
  const classes = useStyles();
  if (isLoading)
    return (
      <div className={classes.container}>
        <div className={classes.loading}>
          <ContentLoader height={60} width={70} speed={1}>
            <rect x="0" y="0" rx="4" ry="4" width="70" height="40" />
            <rect x="15" y="45" rx="4" ry="4" width="40" height="16" />
          </ContentLoader>
        </div>
      </div>
    );

  return (
    <div className={classes.container}>
      <img alt="" src={image} style={{ height: 58, marginBottom: 17 }} />
      <Typography className={classes.btnLabel}>{label}</Typography>
    </div>
  );
};

/**
 * `ListView` tipa `value` como string, pero devuelve el item tal cual lo
 * recibe: acá viaja la persona entera para cargarla al elegirla.
 */
const labelizePersonas = (
  personas: PersonaIdentidad[]
): { label: string; value: string | null }[] => {
  return personas.map((objeto) => {
    return {
      label: objeto.nombres + " " + objeto.apellido,
      value: objeto as unknown as string,
    };
  });
};
